import { html, render } from "lit";
import { IconSpec, MenuItem } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { FolderOptions, NodeModel, NodeSource, TreeOptions, TreeSource } from "@omegagrid/tree";
import rp, { BrowserAdapter } from "@reporting/rp-app-host";
import { Thread, DeploymentThreadTypes} from "@reporting/rp-types/deployment";
import { THREAD_TYPES } from "../types";

export class DeploymentBrowserAdapter extends BrowserAdapter {

	isReadonly = false;

	getDbName(type: DeploymentThreadTypes) {
		const accounting = rp.accountingProvider.accounting;
		switch (type) {
			case DeploymentThreadTypes.Custom:
			case DeploymentThreadTypes.DbData:
				return accounting.data_db_name;
			case DeploymentThreadTypes.DbSuper:
			case DeploymentThreadTypes.Scripts:
				return accounting.super_db_name;
			case DeploymentThreadTypes.DbMaster:
				return accounting.master_db_name || accounting.master_db_name_2;
		}
		return null;
	}

	async getTree(): Promise<TreeSource> {
		const threads = (await rp.ms('deployment').get<Thread[]>(`threads`, {
			fields: ['idz', 'id_thread', 'description', 'type', 'has_doc', 'ord_num'],
			order: [['type', 'asc'], ['description', 'asc'], ['ord_num', 'asc']],
			where: this.isReadonly ? {'type': [DeploymentThreadTypes.DbData, DeploymentThreadTypes.Custom]} : undefined
		})).reduce<Record<string, Thread[]>>((acc, item) => {
			acc[item.type] = acc[item.type] || [];
			acc[item.type].push(item);
			return acc;
		}, {});

		const treeItems: Record<string, NodeSource[]> = {};
		THREAD_TYPES.forEach(type => {
			if (!threads[type.k]) return;
			treeItems[type.k] = [];
			const createdFolders = new Set<string>();
			const folderItems: NodeSource[] = [];
			const items: NodeSource[] = [];

			threads[type.k].forEach(thread => {
				let nodeLvl = 1;
				let threadName = thread.description;
				const nameParts = thread.description.split('/');
				if (nameParts.length > 1) {
					threadName = nameParts.pop();
					nameParts.forEach((folderName, index) => {
						const path = nameParts.slice(0, index + 1).join('/');
						if (!createdFolders.has(path)) {
							createdFolders.add(path);
							folderItems.push({
								k: `deployment-${type.k}-${path}`,
								v: folderName,
								l: index + 1,
								collapsed: true,
								folder: BrowserAdapter.folderOptions,
								props: { path: nameParts, dir: true }
							});
						}
						nodeLvl++;
					});
				}

				const newItem: NodeSource = {
					k: `deployment-thread-${thread.id_thread}`,
					v: threadName,
					l: nodeLvl,
					icon: this.getItemIcon(threadName, thread.type),
					props: { thread, path: nameParts, dir: false }
				};
				if (nodeLvl == 1) items.push(newItem);
				else folderItems.push(newItem);
			});
			treeItems[type.k] = [...folderItems, ...items];
		});

		const tree = THREAD_TYPES.flatMap(type => {
			if (!treeItems[type.k] || treeItems[type.k].length === 0) return [];
			return [{
				k: `deployment-${type.k}`,
				v: type.v,
				l: 0,
				renderer: (div: HTMLDivElement, node: NodeModel) => html`${type.v} (<i style="color:orange">${this.getDbName(type.k)}</i>)`,
				collapsed: true,
				folder: this.getFolderOptions(type.k),
				props: { type: type.k }
			}, ...treeItems[type.k]];
		});

		if (rp.userProvider.isSA()) {
			tree.push({
				k: `super-jobActivityContainer`,
				v: msg('Job activity monitor'),
				l: 0,
				icon: { icon: 'stopwatch', color: 'DarkRed', statusIcon: ['fas', 'database'], statusColor: 'GoldenRod' }
			});
		}

		if (!this.isReadonly) {
			// scheduled tasks of the application servers (DPL_TASK), run by the rp-tasks poller
			tree.push({
				k: `deployment-tasks`,
				v: msg('Tasks'),
				l: 0,
				icon: { icon: 'list-check', color: 'DarkOliveGreen' }
			});
		}

		return tree;
	}

	private getItemIcon(name: string, type?: DeploymentThreadTypes): IconSpec|string {
		if (type === DeploymentThreadTypes.Scripts) return 'file-code';

		const lowerName = name.toLowerCase();
		const icon: IconSpec = { icon: 'database' };
		if (lowerName.startsWith('p_')) {
			icon.statusIcon = 'gear';
			icon.statusColor = '#3c36fe';
		} else if (lowerName.startsWith('f_')) {
			icon.statusIcon = 'function';
			icon.statusColor = '#a83deb';
		}
		return icon;
	}

	private getFolderOptions(type: DeploymentThreadTypes): FolderOptions {
		const s = type === DeploymentThreadTypes.Scripts;
		const color = s ? '#8f8f8f' : '#dcb67a';
		const statusIcon = s ? 'file-code' : 'database';
		const statusColor = s ? '#cdcdcd' : 'GoldenRod';
		return {
			openIcon: {
				icon: ['fas', 'folder-open'],
				color: color,
				statusIcon: ['fas', statusIcon],
				statusColor: statusColor,
			},
			closedIcon: {
				icon: ['fas', 'folder'],
				color: color,
				statusIcon: ['fas', statusIcon],
				statusColor: statusColor,
			},
		};
	}

	getMenuItems(node: NodeModel): MenuItem[] {
		if (this.isReadonly) return [];
		const thread = node.properties?.thread as Thread;
		const items: MenuItem[] = [];
		if (thread) {
			if (rp.host) items.push({ key: 'open', value: msg('Open'), icon: 'book-open' });
			items.push({ key: 'properties', value: msg('Properties'), icon: 'pen-to-square' });
			items.push({ key: 'documentation', value: msg('Documentation'), icon: 'book' });
			items.push({ key: 'duplicate', value: msg('Duplicate'), icon: 'copy' });
			items.push({ key: 'delete', value: msg('Delete'), icon: 'trash', color: '#ff0000' });
		}
		return items;
	}

	async onAction(key: string, node: NodeModel) {
		const thread = node.properties.thread as Thread;
		switch (key) {
			case 'open':
				rp.host.open(node.key, { title: node.value });
				break;
			case 'properties':
				rp.host.openModalSidebar({
					id: `deployment-threadForm-${thread.id_thread}`,
					caption: `#${thread.id_thread} ${thread.description}`
				});
				break;
			case 'documentation':
				rp.host.open(`deployment-docEditor-${thread.id_thread}`, { title: `${node.value} [doc]` });
				break;
			case 'duplicate':
				rp.host.openModalSidebar({
					id: `deployment-threadForm-${thread.id_thread}-copy`,
					caption: `${msg('Copy')}: #${thread.id_thread} ${thread.description}`
				});
				break;
			case 'delete':
				if (thread) {
					const command = await rp.host.openMenu({
						content: 'yes_no',
						text: `${msg('Are you sure you want to delete')} '#${thread.id_thread} ${thread.description}'?`
					});
					if (command?.id == 'Y') {
						await rp.ms('deployment').delete(`threads/${thread.idz}`);
						this.browser.deleteNode(node);
					}
				}
				break;
		}
	}

	getOptions(): Partial<TreeOptions> {
		return this.createOptions({
			contextMenuButton: true,
			selectMode: 'none',
			expanders: false,
			search: true,
			nodeRenderer: (div, node) => {
				const thread = node.properties?.thread as Thread;
				div.style.display = 'flex';
				div.style.flexDirection = 'row';
				render(html`
					<div style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap">${node.value}</div>
					${thread?.has_doc ? html`<og-icon icon="book" style="flex: 0 1 20px; text-align: right;"></og-icon>` : ''}
				`, div);
			},
		});
	}
}
